(() => {
  const globalKey = "LuciaAboutUsModular";
  const selector = "[data-lucia-about-section]";
  const state = window[globalKey] || {
    mounted: new WeakSet(),
    railMounted: new WeakSet(),
    roots: [],
    frame: 0,
    listenersReady: false
  };
  window[globalKey] = state;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const mobileViewport = window.matchMedia("(max-width: 749px)");
  const hoverEnabled = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const refreshRoots = () => {
    state.roots = [...document.querySelectorAll(selector)].filter((root) => root.isConnected);
  };

  const setSectionState = (root) => {
    const section = root.querySelector("[data-section]");
    if (!section || !section.classList.contains("lucia-au-motion-section")) return;
    const rect = section.getBoundingClientRect();
    const triggerValue = mobileViewport.matches ? root.dataset.mobileTrigger : root.dataset.trigger;
    const exitValue = mobileViewport.matches ? root.dataset.mobileExit : root.dataset.exit;
    const trigger = Math.min(95, Math.max(5, Number(triggerValue || 65)));
    const exit = Math.min(80, Math.max(0, Number(exitValue || 20)));
    const triggerLine = window.innerHeight * (trigger / 100);
    const exitLine = window.innerHeight * (exit / 100);
    const future = rect.top > triggerLine;
    const past = rect.bottom <= exitLine;
    section.classList.toggle("lucia-au-is-future", future);
    section.classList.toggle("lucia-au-is-past", past);
    section.classList.toggle("lucia-au-is-active", !future && !past);
    section.classList.remove("lucia-au-is-handoff");
  };

  const currentIndex = () => {
    const line = window.innerHeight * .42;
    let active = 0;
    state.roots.forEach((root, index) => {
      if (root.getBoundingClientRect().top <= line) active = index;
    });
    return active;
  };

  const updateRail = () => {
    const active = currentIndex();
    document.querySelectorAll("[data-lucia-progress]").forEach((rail) => {
      const buttons = [...rail.querySelectorAll("button")];
      rail.style.setProperty("--progress", state.roots.length > 1 ? ((active / (state.roots.length - 1)) * 100) + "%" : "0%");
      buttons.forEach((button, index) => {
        button.classList.toggle("lucia-au-active", index === active);
        if (index === active) button.setAttribute("aria-current", "step");
        else button.removeAttribute("aria-current");
      });
    });
  };

  const render = () => {
    state.frame = 0;
    refreshRoots();
    state.roots.forEach(setSectionState);
    updateRail();
  };

  const schedule = () => {
    if (!state.frame) state.frame = requestAnimationFrame(render);
  };

  const mountRail = (rail) => {
    refreshRoots();
    rail.replaceChildren(...state.roots.map((root, index) => {
      const label = root.dataset.navLabel || "Section " + (index + 1);
      const button = document.createElement("button");
      button.className = "lucia-au-progress-rail__button";
      button.type = "button";
      button.title = label;
      button.setAttribute("aria-label", label);
      button.innerHTML = '<span class="lucia-au-progress-rail__label"></span>';
      button.querySelector("span").textContent = label;
      button.addEventListener("click", () => {
        refreshRoots();
        state.roots[index]?.scrollIntoView({ behavior: reduceMotion.matches ? "auto" : "smooth", block: "start" });
      });
      return button;
    }));
  };

  const setupDisclosure = (root, buttonSelector, containerSelector) => {
    const buttons = [...root.querySelectorAll(buttonSelector)];
    const activate = (button) => {
      const container = button.closest(containerSelector);
      if (!container) return;
      const siblings = [...container.querySelectorAll(buttonSelector)];
      const selected = siblings.indexOf(button);
      if (selected < 0) return;
      container.dataset.activePanel = String(selected);
      siblings.forEach((item, index) => {
        const active = index === selected;
        item.classList.toggle("lucia-au-is-active", active);
        item.setAttribute("aria-expanded", String(active));
        const panelId = item.getAttribute("aria-controls");
        const panel = panelId ? root.querySelector('[id="' + panelId + '"]') : null;
        if (panel) panel.setAttribute("aria-hidden", String(!active));
      });
      const stageId = container.dataset.mediaStage;
      const stage = stageId ? root.querySelector('[id="' + stageId + '"]') : null;
      stage?.querySelectorAll("[data-panel-index]").forEach((slide) => {
        const active = Number(slide.dataset.panelIndex) === selected;
        slide.classList.toggle("lucia-au-is-active", active);
        slide.setAttribute("aria-hidden", String(!active));
      });
    };
    buttons.forEach((button) => {
      button.addEventListener("click", () => activate(button));
      button.addEventListener("focus", () => activate(button));
      if (hoverEnabled) button.addEventListener("pointerenter", () => activate(button));
    });
  };

  const setupHomes = (root) => {
    const gallery = root.querySelector(".lucia-au-homes-gallery");
    const track = gallery?.querySelector("[data-homes-track]");
    const loop = track?.querySelector('[data-homes-loop="primary"]');
    const section = root.querySelector(".lucia-au-story-section--homes");
    if (!gallery || !track || !loop || !section || track.querySelector('[data-homes-loop="clone"]')) return;
    const clone = loop.cloneNode(true);
    clone.dataset.homesLoop = "clone";
    clone.setAttribute("aria-hidden", "true");
    clone.querySelectorAll("a").forEach((link) => link.setAttribute("tabindex", "-1"));
    clone.querySelectorAll("img").forEach((image) => { image.alt = ""; image.draggable = false; });
    track.append(clone);
    track.querySelectorAll("img").forEach((image) => { image.draggable = false; });

    let width = 0;
    let offset = 0;
    let dragging = false;
    let hovered = false;
    let focused = false;
    let moved = false;
    let startX = 0;
    let startOffset = 0;
    let lastFrame = performance.now();
    const speed = Math.max(15, Number(root.dataset.carouselSpeed || 46)) * 1000;
    const normalize = () => {
      if (!width) return;
      while (offset <= -width) offset += width;
      while (offset > 0) offset -= width;
    };
    const paint = () => { track.style.transform = "translate3d(" + offset + "px, 0, 0)"; };
    const measure = () => { width = loop.getBoundingClientRect().width; normalize(); paint(); };
    gallery.addEventListener("pointerdown", (event) => {
      if (!event.isPrimary || (event.pointerType === "mouse" && event.button !== 0)) return;
      dragging = true; moved = false; startX = event.clientX; startOffset = offset;
      gallery.setPointerCapture(event.pointerId);
    });
    gallery.addEventListener("pointermove", (event) => {
      if (!dragging || !event.isPrimary) return;
      const distance = event.clientX - startX;
      if (Math.abs(distance) > 5) { moved = true; gallery.classList.add("lucia-au-is-dragging"); }
      offset = startOffset + distance; normalize(); paint(); event.preventDefault();
    });
    const finish = (event) => {
      if (!dragging) return;
      dragging = false; gallery.classList.remove("lucia-au-is-dragging");
      if (gallery.hasPointerCapture(event.pointerId)) gallery.releasePointerCapture(event.pointerId);
      lastFrame = performance.now(); setTimeout(() => { moved = false; }, 0);
    };
    gallery.addEventListener("pointerup", finish);
    gallery.addEventListener("pointercancel", finish);
    gallery.addEventListener("pointerenter", () => { hovered = true; });
    gallery.addEventListener("pointerleave", () => { hovered = false; });
    gallery.addEventListener("focusin", () => { focused = true; });
    gallery.addEventListener("focusout", (event) => { focused = gallery.contains(event.relatedTarget); });
    gallery.addEventListener("dragstart", (event) => event.preventDefault());
    gallery.addEventListener("click", (event) => { if (moved) { event.preventDefault(); event.stopPropagation(); } }, true);
    const animate = (timestamp) => {
      const elapsed = Math.min(timestamp - lastFrame, 40);
      if (!reduceMotion.matches && !dragging && !hovered && !focused && section.classList.contains("lucia-au-is-active") && width) {
        offset -= elapsed * (width / speed); normalize(); paint();
      }
      lastFrame = timestamp;
      requestAnimationFrame(animate);
    };
    measure();
    new ResizeObserver(measure).observe(loop);
    requestAnimationFrame(animate);
  };

  const mount = (scope = document) => {
    scope.querySelectorAll?.(selector).forEach((root) => {
      if (state.mounted.has(root)) return;
      state.mounted.add(root);
      root.dataset.initialized = "true";
      setupDisclosure(root, ".lucia-au-choice-panel", ".lucia-au-choice-panels");
      setupDisclosure(root, ".lucia-au-promise-card", ".lucia-au-promise-cards");
      setupHomes(root);
    });
    document.querySelectorAll("[data-lucia-progress]").forEach(mountRail);
    schedule();
  };

  if (!state.listenersReady) {
    state.listenersReady = true;
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    document.addEventListener("shopify:section:load", (event) => mount(event.target));
    document.addEventListener("shopify:section:unload", schedule);
  }
  mount();
})();
